import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { authService, roomTypeService } from '../../../services/api';
import { ROOM_TYPE_OPTIONS } from '../../../constants/roomTypes';

const RoomForm = ({ room, isOpen, onClose, onSave, mode = 'create' }) => {
  const [formData, setFormData] = useState({
    number: '',
    type: 'STANDARD',
    floor: '1',
    capacity: '2',
    beds: '1',
    area: '25',
    bedsSolteiro: '0',
    bedsCasal: '0',
    bedsQueen: '0',
    bedsKing: '0',
    bedsBeliche: '0',
    singlePrice: '',
    couplePrice: '',
    hasAC: true,
    hasMinibar: false,
    hasBathroom: true,
    status: 'AVAILABLE',
    description: '',
    hotelId: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedHotel, setSelectedHotel] = useState(null);
  const [roomTypes, setRoomTypes] = useState([]);
  const [loadingTypes, setLoadingTypes] = useState(false);

  // Carregar tipos de quarto da API
  useEffect(() => {
    const loadRoomTypes = async () => {
      try {
        setLoadingTypes(true);
        console.log('🔄 RoomForm: Carregando tipos de quarto da API...');
        const types = await roomTypeService.getAll();
        console.log('✅ RoomForm: Tipos de quarto carregados:', types);
        setRoomTypes(types);
      } catch (error) {
        console.error('❌ RoomForm: Erro ao carregar tipos de quarto:', error);
        // Fallback para tipos estáticos
        const fallbackTypes = ROOM_TYPE_OPTIONS.map(opt => ({ name: opt.value, label: opt.label }));
        console.log('⚠️ RoomForm: Usando tipos estáticos como fallback:', fallbackTypes);
        setRoomTypes(fallbackTypes);
      } finally {
        setLoadingTypes(false);
      }
    };

    if (isOpen) {
      loadRoomTypes();
    }
  }, [isOpen]);

  useEffect(() => {
    // Obter hotel selecionado
    const hotel = authService.getSelectedHotel();
    setSelectedHotel(hotel);
    
    if (hotel) {
      setFormData(prev => ({
        ...prev,
        hotelId: hotel.id
      }));
    }
  }, []);

  useEffect(() => {
    // Função auxiliar para extrair o nome do tipo
    const getRoomTypeName = (type) => {
      console.log('🔍 RoomForm: Extraindo tipo do quarto:', type);
      if (!type) return 'STANDARD';
      
      // Se for string, retorna diretamente
      if (typeof type === 'string') return type;
      
      // Se for objeto, extrai o name
      if (typeof type === 'object' && type !== null) {
        const extractedName = type.name || type.value || 'STANDARD';
        console.log('🔍 RoomForm: Tipo extraído do objeto:', extractedName);
        return extractedName;
      }
      
      return 'STANDARD';
    };

    if (room && mode === 'edit') {
      console.log('🔍 RoomForm: Editando quarto:', room);
      setFormData({
        number: room.number || '',
        type: getRoomTypeName(room.type),
        floor: room.floor?.toString() || '1',
        capacity: room.capacity?.toString() || '2',
        beds: room.beds?.toString() || '1',
        area: room.area?.toString() || '25',
        bedsSolteiro: room.bedsSolteiro?.toString() ?? '0',
        bedsCasal: room.bedsCasal?.toString() ?? '0',
        bedsQueen: room.bedsQueen?.toString() ?? '0',
        bedsKing: room.bedsKing?.toString() ?? '0',
        bedsBeliche: room.bedsBeliche?.toString() ?? '0',
        singlePrice: room.singlePrice?.toString() || '',
        couplePrice: room.couplePrice?.toString() || '',
        hasAC: room.hasAC ?? true,
        hasMinibar: room.hasMinibar ?? false,
        hasBathroom: room.hasBathroom ?? true,
        status: room.status || 'AVAILABLE',
        description: room.description || '',
        hotelId: room.hotelId || selectedHotel?.id || ''
      });
    } else if (mode === 'duplicate' && room) {
      setFormData({
        number: '',
        type: getRoomTypeName(room.type),
        floor: room.floor?.toString() || '1',
        capacity: room.capacity?.toString() || '2',
        beds: room.beds?.toString() || '1',
        area: room.area?.toString() || '25',
        bedsSolteiro: room.bedsSolteiro?.toString() ?? '0',
        bedsCasal: room.bedsCasal?.toString() ?? '0',
        bedsQueen: room.bedsQueen?.toString() ?? '0',
        bedsKing: room.bedsKing?.toString() ?? '0',
        bedsBeliche: room.bedsBeliche?.toString() ?? '0',
        singlePrice: room.singlePrice?.toString() || '',
        couplePrice: room.couplePrice?.toString() || '',
        hasAC: room.hasAC ?? true,
        hasMinibar: room.hasMinibar ?? false,
        hasBathroom: room.hasBathroom ?? true,
        status: 'AVAILABLE',
        description: room.description || '',
        hotelId: room.hotelId || selectedHotel?.id || ''
      });
    } else {
      setFormData({
        number: '',
        type: 'STANDARD',
        floor: '1',
        capacity: '2',
        beds: '1',
        area: '25',
        bedsSolteiro: '0',
        bedsCasal: '0',
        bedsQueen: '0',
        bedsKing: '0',
        bedsBeliche: '0',
        singlePrice: '',
        couplePrice: '',
        hasAC: true,
        hasMinibar: false,
        hasBathroom: true,
        status: 'AVAILABLE',
        description: '',
        hotelId: selectedHotel?.id || ''
      });
    }
    setErrors({});
  }, [room, mode, isOpen, selectedHotel]);

  const statusOptions = [
    { value: 'AVAILABLE', label: 'Disponível' },
    { value: 'OCCUPIED', label: 'Ocupado' },
    { value: 'MAINTENANCE', label: 'Manutenção' },
    { value: 'CLEANING', label: 'Limpeza' },
    { value: 'RESERVED', label: 'Reservado' }
  ];

  const validateForm = () => {
    const newErrors = {};

    if (!formData.number.trim()) {
      newErrors.number = 'Número do quarto é obrigatório';
    }

    if (!formData.hotelId) {
      newErrors.hotelId = 'Hotel é obrigatório';
    }

    if (!formData.singlePrice || parseFloat(formData.singlePrice) <= 0) {
      newErrors.singlePrice = 'Preço para solteiro deve ser maior que zero';
    }

    if (!formData.couplePrice || parseFloat(formData.couplePrice) <= 0) {
      newErrors.couplePrice = 'Preço para casal deve ser maior que zero';
    }

    if (parseInt(formData.capacity) < 1 || parseInt(formData.capacity) > 10) {
      newErrors.capacity = 'Capacidade deve estar entre 1 e 10 pessoas';
    }

    const totalBeds = (parseInt(formData.bedsSolteiro) || 0) + (parseInt(formData.bedsCasal) || 0) +
      (parseInt(formData.bedsQueen) || 0) + (parseInt(formData.bedsKing) || 0) + (parseInt(formData.bedsBeliche) || 0);
    if (totalBeds < 0) {
      newErrors.bedsSolteiro = 'Quantidade de camas não pode ser negativa';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const bedsSolteiro = parseInt(formData.bedsSolteiro) || 0;
      const bedsCasal = parseInt(formData.bedsCasal) || 0;
      const bedsQueen = parseInt(formData.bedsQueen) || 0;
      const bedsKing = parseInt(formData.bedsKing) || 0;
      const bedsBeliche = parseInt(formData.bedsBeliche) || 0;
      const totalBeds = bedsSolteiro + bedsCasal + bedsQueen + bedsKing + bedsBeliche;

      const roomData = {
        ...formData,
        floor: parseInt(formData.floor),
        capacity: parseInt(formData.capacity),
        beds: totalBeds || parseInt(formData.beds) || 1,
        bedsSolteiro,
        bedsCasal,
        bedsQueen,
        bedsKing,
        bedsBeliche,
        area: parseFloat(formData.area),
        singlePrice: parseFloat(formData.singlePrice),
        couplePrice: parseFloat(formData.couplePrice),
        hotelId: formData.hotelId
      };

      if (mode === 'edit') {
        roomData.id = room.id;
      }

      await onSave(roomData);
      onClose();
    } catch (error) {
      console.error('Erro ao salvar quarto:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  if (!isOpen) return null;

  // Verificar se há hotel selecionado
  if (!selectedHotel) {
    return (
      <>
        <div className="fixed inset-0 bg-black/50 z-50" onClick={onClose} />
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="bg-background border border-border rounded-lg shadow-elevation-modal w-full max-w-md p-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-warning/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="AlertTriangle" size={24} color="var(--color-warning)" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">
                Hotel Não Selecionado
              </h3>
              <p className="text-muted-foreground mb-4">
                Selecione um hotel no cabeçalho antes de criar um quarto.
              </p>
              <Button onClick={onClose} variant="default">
                Fechar
              </Button>
            </div>
          </div>
        </div>
      </>
    );
  }

  const getTitle = () => {
    switch (mode) {
      case 'edit':
        return 'Editar Quarto';
      case 'duplicate':
        return 'Duplicar Quarto';
      default:
        return 'Adicionar Novo Quarto';
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 z-50"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-background border border-border rounded-lg shadow-elevation-modal w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <form onSubmit={handleSubmit}>
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-border">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="Bed" size={20} color="var(--color-primary)" />
                </div>
                <h2 className="text-xl font-semibold text-foreground">{getTitle()}</h2>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={onClose}
                iconName="X"
                className="p-2"
              />
            </div>

            {/* Form Content */}
            <div className="p-6 space-y-6">
              {/* Hotel Selection Info */}
              {selectedHotel && (
                <div className="bg-primary/10 rounded-lg p-4 border border-primary/20">
                  <div className="flex items-center space-x-2">
                    <Icon name="Building2" size={16} color="var(--color-primary)" />
                    <span className="text-sm font-medium text-primary">
                      Hotel: {selectedHotel.name} ({selectedHotel.code})
                    </span>
                  </div>
                  <input type="hidden" value={selectedHotel.id} />
                </div>
              )}

              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Número do Quarto"
                  type="text"
                  value={formData.number}
                  onChange={(e) => handleInputChange('number', e.target.value)}
                  error={errors.number}
                  placeholder="Ex: 101, 205A"
                  required
                />

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Tipo de Quarto {loadingTypes && <span className="text-xs text-muted-foreground">(Carregando...)</span>}
                  </label>
                  <select
                    value={formData.type}
                    onChange={(e) => {
                      console.log('🔄 RoomForm: Tipo selecionado:', e.target.value);
                      handleInputChange('type', e.target.value);
                    }}
                    className="w-full px-3 py-2 border border-border rounded-lg bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                    disabled={loadingTypes}
                  >
                    {(() => {
                      console.log('🔍 RoomForm: Renderizando select de tipos');
                      console.log('  - loadingTypes:', loadingTypes);
                      console.log('  - roomTypes.length:', roomTypes.length);
                      console.log('  - roomTypes:', roomTypes);
                      console.log('  - formData.type:', formData.type);
                      
                      if (loadingTypes) {
                        return <option value="">Carregando tipos...</option>;
                      }
                      
                      if (roomTypes.length === 0) {
                        return <option value="">Nenhum tipo disponível</option>;
                      }
                      
                      return roomTypes.map((type) => {
                        console.log('  - Criando option para tipo:', type);
                        return (
                          <option key={type.id || type.name} value={type.name}>
                            {type.label}
                          </option>
                        );
                      });
                    })()}
                  </select>
                </div>
              </div>

              {/* Capacity and Physical Details */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Input
                  label="Andar"
                  type="number"
                  value={formData.floor}
                  onChange={(e) => handleInputChange('floor', e.target.value)}
                  min="1"
                  max="50"
                />

                <Input
                  label="Capacidade"
                  type="number"
                  value={formData.capacity}
                  onChange={(e) => handleInputChange('capacity', e.target.value)}
                  error={errors.capacity}
                  min="1"
                  max="10"
                  required
                />

                <Input
                  label="Área (m²)"
                  type="number"
                  value={formData.area}
                  onChange={(e) => handleInputChange('area', e.target.value)}
                  min="10"
                  max="200"
                />
              </div>

              {/* Configuração de Camas */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-3">
                  Configuração de Camas
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
                  <Input
                    label="Solteiro"
                    type="number"
                    value={formData.bedsSolteiro}
                    onChange={(e) => handleInputChange('bedsSolteiro', e.target.value)}
                    error={errors.bedsSolteiro}
                    min="0"
                    max="10"
                  />
                  <Input
                    label="Casal"
                    type="number"
                    value={formData.bedsCasal}
                    onChange={(e) => handleInputChange('bedsCasal', e.target.value)}
                    min="0"
                    max="10"
                  />
                  <Input
                    label="Queen"
                    type="number"
                    value={formData.bedsQueen}
                    onChange={(e) => handleInputChange('bedsQueen', e.target.value)}
                    min="0"
                    max="10"
                  />
                  <Input
                    label="King"
                    type="number"
                    value={formData.bedsKing}
                    onChange={(e) => handleInputChange('bedsKing', e.target.value)}
                    min="0"
                    max="10"
                  />
                  <Input
                    label="Beliche"
                    type="number"
                    value={formData.bedsBeliche}
                    onChange={(e) => handleInputChange('bedsBeliche', e.target.value)}
                    min="0"
                    max="10"
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Total: {(parseInt(formData.bedsSolteiro) || 0) + (parseInt(formData.bedsCasal) || 0) +
                    (parseInt(formData.bedsQueen) || 0) + (parseInt(formData.bedsKing) || 0) +
                    (parseInt(formData.bedsBeliche) || 0)} camas
                </p>
              </div>

              {/* Pricing */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Preço Solteiro (R$)"
                  type="number"
                  value={formData.singlePrice}
                  onChange={(e) => handleInputChange('singlePrice', e.target.value)}
                  error={errors.singlePrice}
                  step="0.01"
                  min="0"
                  placeholder="0,00"
                  required
                />

                <Input
                  label="Preço Casal (R$)"
                  type="number"
                  value={formData.couplePrice}
                  onChange={(e) => handleInputChange('couplePrice', e.target.value)}
                  error={errors.couplePrice}
                  step="0.01"
                  min="0"
                  placeholder="0,00"
                  required
                />
              </div>

              {/* Amenities */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-3">
                  Comodidades
                </label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.hasAC}
                      onChange={(e) => handleInputChange('hasAC', e.target.checked)}
                      className="rounded border-border"
                    />
                    <Icon name="Wind" size={16} color="var(--color-primary)" />
                    <span className="text-sm text-foreground">Ar Condicionado</span>
                  </label>

                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.hasMinibar}
                      onChange={(e) => handleInputChange('hasMinibar', e.target.checked)}
                      className="rounded border-border"
                    />
                    <Icon name="Coffee" size={16} color="var(--color-secondary)" />
                    <span className="text-sm text-foreground">Frigobar</span>
                  </label>

                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.hasBathroom}
                      onChange={(e) => handleInputChange('hasBathroom', e.target.checked)}
                      className="rounded border-border"
                    />
                    <Icon name="Bath" size={16} color="var(--color-accent)" />
                    <span className="text-sm text-foreground">Banheiro Privativo</span>
                  </label>
                </div>
              </div>

              {/* Status */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Status
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => handleInputChange('status', e.target.value)}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  {statusOptions.map((status) => (
                    <option key={status.value} value={status.value}>
                      {status.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Descrição (Opcional)
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Descrição adicional do quarto..."
                  rows={3}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                />
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end space-x-3 p-6 border-t border-border">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                variant="default"
                loading={isSubmitting}
                iconName="Save"
                iconPosition="left"
              >
                {mode === 'edit' ? 'Salvar Alterações' : 'Adicionar Quarto'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default RoomForm;